import React, { useEffect, useState } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { gql, useQuery } from '@apollo/client'
import Header from './components/Header'
import Footer from './components/Footer'
import HomePage from './pages/HomePage'
import ProductsPage from './pages/ProductsPage'
import ContactPage from './pages/ContactPage'
import PaymentPage from './pages/PaymentPage'
import OrderStatusPage from './pages/OrderStatusPage'
import AboutUsPage from './pages/AboutUsPage'
import TermsPage from './pages/TermsPage'
import PrivacyPolicyPage from './pages/PrivacyPolicyPage'
import DataDeletionPage from './pages/DataDeletionPage'
import ProfilePage from './pages/ProfilePage'
import logoImage from './images/morecharz-round-logo.webp'
import './App.css'

const GET_ME = gql`
  query GetMe {
    me {
      id
      phone
      name
      email
      authProvider
      role
      avatarUrl
    }
  }
`

function App() {
  const [user, setUser] = useState(null)

  const getStoredToken = () => {
    if (typeof window === 'undefined') return null
    return window.__SESSION_TOKEN__ || sessionStorage.getItem('authToken') || localStorage.getItem('token') || localStorage.getItem('authToken') || null
  }

  // Check persisted token or in-memory session token
  const token = getStoredToken()
  const { data, error, loading } = useQuery(GET_ME, {
    skip: !token,
    fetchPolicy: 'network-only'
  })

  useEffect(() => {
    if (data?.me) {
      setUser(data.me)
      return
    }

    if (data && !data.me && token) {
      localStorage.removeItem('token')
      setUser(null)
      return
    }

    if (error) {
      localStorage.removeItem('token')
      setUser(null)
    }
  }, [data, error])

  // remember: boolean — if true persist token across sessions; otherwise keep in-memory
  const handleAuthSuccess = (authUser, authToken, remember = true) => {
    delete window.__PROFILE_TOKEN__
    if (authToken) {
      if (remember) {
        localStorage.setItem('token', authToken)
        localStorage.removeItem('authToken')
        sessionStorage.removeItem('authToken')
        delete window.__SESSION_TOKEN__
      } else if (typeof window !== 'undefined') {
        // store transient session token for current tab/process
        window.__SESSION_TOKEN__ = authToken
        sessionStorage.setItem('authToken', authToken)
        localStorage.removeItem('authToken')
      }
    }
    // Clear any explicit "user logged out" flag so social SDKs may auto-login again
    try {
      if (typeof window !== 'undefined' && window.sessionStorage) {
        window.sessionStorage.removeItem('userLoggedOut')
      }
    } catch (e) { }

    setUser(authUser)
  }

  const handleLogout = () => {
    // Clear persisted and in-memory session tokens
    if (typeof window !== 'undefined') {
      localStorage.removeItem('token')
      localStorage.removeItem('authToken')
      sessionStorage.removeItem('authToken')
      try {
        delete window.__SESSION_TOKEN__
      } catch (e) { }
      try {
        if (window.sessionStorage) {
          // Prevent social SDKs from auto-signing-in immediately after logout
          window.sessionStorage.setItem('userLoggedOut', '1')
        }
      } catch (e) { }
      // If FB SDK present, also ask it to logout to avoid immediate re-auth flows
      try {
        if (window.FB && typeof window.FB.getLoginStatus === 'function') {
          window.FB.getLoginStatus((resp) => {
            if (resp?.status === 'connected' && typeof window.FB.logout === 'function') {
              window.FB.logout(() => { })
            }
          })
        }
      } catch (e) { }
    }
    setUser(null)
  }

  const handleUserUpdate = (updatedUser) => {
    setUser((currentUser) => currentUser ? { ...currentUser, ...updatedUser } : updatedUser)
  }

  const sessionCheckPending = token && !user && (loading || (!error && Boolean(data?.me)))
  if (sessionCheckPending) {
    return (
      <div className="session-loading" role="status" aria-label="Loading your account" aria-live="polite">
        <div className="session-loading-mark">
          <span className="session-loading-ring" aria-hidden="true" />
          <img src={logoImage} alt="" className="session-loading-logo" />
        </div>
      </div>
    )
  }

  return (
    <Router>
      <div className="app">
        <Header user={user} onLogout={handleLogout} />
        <main className="main-content">
          <Routes>
            <Route path="/" element={<HomePage user={user} onAuthSuccess={handleAuthSuccess} />} />
            <Route path="/products" element={<ProductsPage user={user} isLoggedIn={Boolean(user)} />} />
            <Route path="/recharge" element={<Navigate to="/products" replace />} />
            <Route path="/payment/:orderId" element={<PaymentPage />} />
            <Route path="/order-status/:orderId" element={<OrderStatusPage />} />
            <Route path="/about" element={<AboutUsPage />} />
            <Route path="/contact" element={<ContactPage user={user} />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/data-deletion" element={<DataDeletionPage />} />
            <Route path="/profile" element={<ProfilePage user={user} onUserUpdate={handleUserUpdate} onAuthSuccess={handleAuthSuccess} />} />
            <Route path="/payment-success" element={<div className="container"><h1>Payment Successful!</h1><p>Your recharge will be processed shortly.</p></div>} />
            <Route path="/payment-failed" element={<div className="container"><h1>Payment Failed</h1><p>Please try again.</p></div>} />
          </Routes>
        </main>
        <Footer />
      </div>
    </Router>
  )
}

export default App
